// src/portal/PortalShellSelect.jsx
//
// WHICH SHELL A PORTAL ROUTE MOUNTS.
//
// ── THE PROBLEM THIS SOLVES ─────────────────────────────────────────────────
//
// PortalShell was built for a confined submitter -- a third party inside a
// customer's org with no business seeing platform chrome, and no ability to
// load it without collecting 403s. That was right, and it is still right for
// them.
//
// It was then also what an ENTITLED reviewer got. An Earl analyst opening a
// plan review is an ordinary member of their org holding their org's products,
// and the portal handed them a thin bar and a bare content area -- a surface
// that looks like a different application from the rest of AI PRIME, reached
// from inside AI PRIME.
//
// So: entitled org users get the app shell. Confined submitters keep this one.
//
// ── WHY THIS IS NOT THE FLAG-INSIDE-AppShell THAT IS FORBIDDEN ──────────────
//
// WorkspaceShell.jsx:5-24 and PortalShell.jsx:5-19 both record the rule: shell
// selection is STRUCTURAL, never a conditional, because AppShell calls
// useOperatingAoiInit, useRestrictedCaseSelection, useRestrictedAutoEnableImagery,
// useRestrictedPadDrivenImagery, useRestrictedInitialLayers and
// useImpersonationFromUrl at the top of its body, and HOOKS RUN BEFORE ANY
// CONDITIONAL RETURN. An `if (isPortal)` inside AppShell fires all six anyway.
//
// THE RULE IS ABOUT WHERE THE CONDITIONAL SITS RELATIVE TO THE MOUNT, and this
// one sits ABOVE it. React does not run the hooks of a component it does not
// render. The forbidden shape returns early from inside a body whose hooks have
// already executed; this one decides which body executes at all. The difference
// is not stylistic -- it is the whole of the guarantee, and
// PortalShellSelect.test.jsx counts the six hooks rather than trusting it.
//
// ── WHY NOT A THIRD LAYOUT ROUTE ────────────────────────────────────────────
//
// _platform, _workspace and _portal are pathless layout routes and the router
// picks between them BY PATH. Both kinds of user arrive at the same `/portal`,
// so there is no fourth path to hang a fourth layout on, and two layout routes
// claiming one path is an ambiguous match rather than a choice. The predicate
// is not a path, so it cannot be expressed as routing. It is expressed here,
// at the one point where the two shells are still both possible.
//
// ── THE PREDICATE IS THE SERVER'S, NOT A SECOND COPY OF IT ──────────────────
//
// `home_route` is computed by admin-api/lib/homeRoute.js out of
// restrictedClassFor -- the same predicate that drives the server's own request
// allowlist. Asking the profile for it means the shell and the allowlist cannot
// disagree about who is confined.
//
// ── THE VALUE IS A LITERAL, AND THAT IS NOT A FREE CHOICE ───────────────────
//
// The first cut imported RESTRICTED_CLASSES and read `.portal.home`, so that
// the portal's home was spelled once, on the server. THE IMAGE BUILD REFUSED
// IT, and the refusal is correct:
//
//   Could not resolve ../auth.js from admin-api/middleware/restrictedAccount.js
//
// restrictedAccount.js is not a leaf. verifyBearer reaches auth.js through a
// lazy dynamic import -- lazy on purpose, so the pure predicates stay testable
// without Keycloak -- and a lazy import is still a static edge to Rollup, while
// auth.js throws at import time without KC_ISSUER and has no business in a
// browser bundle.
//
// (Written as prose rather than as the call. gen-ui-graph.mjs:163 scans raw
// text for that form, COMMENTS INCLUDED and deliberately so -- it is how JSDoc
// `@typedef {import(...)}` dependencies stay visible -- and spelling it here
// gave PortalShellSelect.jsx a dependency on a file it does not import.)
//
// That is why this module cannot join caseStages.js and submitterTags.js as a
// named build context: those are leaves and this is not.
// Vitest never trips on it because the branch never executes; `npm run build`
// on this machine never tripped on it either. Only the IMAGE build did, which
// is the "green locally, broken in the artifact" shape ui/Dockerfile:8-13
// already names.
//
// So the client keeps the literal -- ONCE, here, and router.jsx's HOMES table
// imports it rather than spelling it a second time -- and a TEST pins it to the
// server's own value. That is the mechanism already used for this exact
// contract: router.authMeContract.test.jsx exists because the client's routing
// table and homeRouteFor could drift, and it closes that by calling the real
// homeRouteFor rather than by making the client import it at runtime.
// PortalShellSelect.test.jsx asserts PORTAL_HOME against BOTH
// RESTRICTED_CLASSES.portal.home and homeRouteFor's output for a plan_submitter.
import AppShell from "../App";
import PortalShell from "./PortalShell";
import { useAuthStore } from "../stores/useAuthStore";

/** The server's home for the confined portal class. Pinned to
 *  RESTRICTED_CLASSES.portal.home AND to homeRouteFor's real output by
 *  PortalShellSelect.test.jsx.
 *
 *  IT IS THE LIST, NOT /portal. /portal is a launcher with a card per portal
 *  surface; a submitter has one surface, so the launcher would be a click in
 *  the way. When this moved, the pin caught the client half before it shipped
 *  -- and so did the shell branch, because a stale value here makes
 *  `home_route === PORTAL_HOME` false for a confined account and mounts
 *  AppShell, firing all six platform hooks it exists to prevent. */
export const PORTAL_HOME = "/portal/plan-reviews";

/**
 * @param {object|null} profile  /auth/me's user, or null while it is in flight
 * @returns {'portal'|'platform'}
 *
 * FAIL NARROW, IN BOTH UNSETTLED CASES.
 *
 * A profile that has not landed, and a profile the server never classified (no
 * `home_route` key -- an API older than this UI), both answer 'portal'. The two
 * errors are not symmetric: mounting AppShell for a confined account fires six
 * hooks that 403 and renders as a broken page, which is the failure this whole
 * design exists to prevent. Mounting PortalShell for an entitled one is a thin
 * header for as long as it takes /auth/me to answer.
 *
 * In practice neither arm is reached on a cold load: requireAuthAndAllowedRoute
 * awaits loadProfile() in beforeLoad, and beforeLoad settles before the layout
 * renders. They are the net, not the path.
 */
export function shellFor(profile) {
  if (!profile) return "portal";
  if (!("home_route" in profile)) return "portal";
  return profile.home_route === PORTAL_HOME ? "portal" : "platform";
}

export default function PortalShellSelect() {
  const profile = useAuthStore((s) => s.profile);
  return shellFor(profile) === "portal" ? <PortalShell /> : <AppShell />;
}
